/**-----------------------------------------------------------------------------------------
* Copyright © 2021 Progress Software Corporation. All rights reserved.
* Licensed under commercial license. See LICENSE.md in the project root for more information
*-------------------------------------------------------------------------------------------*/
import { __decorate, __param, __metadata } from 'tslib';
import { Injectable, InjectionToken, Inject, Optional } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { skip, map, tap } from 'rxjs/operators';

/* tslint:disable:max-line-length */
/**
 * A base class for a service that returns localized messages.
 *
 * For more information, refer to the section on [using the message service]({% slug messages_l10n %}#toc-using-the-message-service).
 */
import * as ɵngcc0 from '@angular/core';
let MessageService = class MessageService {
    /* tslint:disable:max-line-length */
    /**
     * A base class for a service that returns localized messages.
     *
     * For more information, refer to the section on [using the message service]({% slug messages_l10n %}#toc-using-the-message-service).
     */
    constructor() {
        /**
         * @hidden
         */
        this.changes = new BehaviorSubject({ rtl: undefined });
    }
    /**
     * Notifies the components that the messages were changed.
     *
     * @param rtl - (Optional) A new value for the [text direction token]({% slug api_l10n_rtl %}).
     */
    notify(rtl) {
        this.changes.next({ rtl });
    }
    /**
     * Returns a localized message for the supplied key.
     *
     * @param key - The message key. For example, `"kendo.grid.noRecords"`.
     * @return - The localized message for this key or `undefined` if not found.
     */
    get(key) {
        return undefined;
    }
};
MessageService.ɵfac = function MessageService_Factory(t) { return new (t || MessageService)(); };
MessageService.ɵprov = ɵngcc0.ɵɵdefineInjectable({ token: MessageService, factory: function (t) { return MessageService.ɵfac(t); } });

/**
 * Base class that acts as a component messages container.
 *
 * For internal use.
 * @hidden
 */
class ComponentMessages {
    get override() {
        return false;
    }
    ngOnChanges(changes) {
        this.register(changes);
        if (Object.keys(changes).some(field => !changes[field].isFirstChange())) {
            this.service.notifyChanges();
        }
    }
    ngOnInit() {
        this.subscription = this.service.changes.pipe(skip(1)).subscribe(() => this.register(this));
    }
    register(changes) {
        const keys = Object.keys(changes);
        keys.forEach(key => this.service.register(key, this[key], this.override));
    }
    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }
}
ComponentMessages.ɵfac = function ComponentMessages_Factory(t) { return new (t || ComponentMessages)(); };
ComponentMessages.ɵdir = ɵngcc0.ɵɵdefineDirective({ type: ComponentMessages, features: [ɵngcc0.ɵɵNgOnChangesFeature] });

/**
 * A token that specifies the text direction of Kendo UI for Angular components.
 *
 * @example
 * {% embed_file rtl/app.module.ts preview %}
 * {% embed_file rtl/app.component.ts %}
 * {% embed_file shared/main.ts hidden %}
 *
 */
const RTL = new InjectionToken("Kendo UI Right-to-Left token");

/**
 * Localization prefix for the component messages.
 *
 * For internal use.
 * @hidden
 */
const L10N_PREFIX = new InjectionToken('Localization key prefix');
/**
 * Component localization service.
 *
 * For internal use.
 * @hidden
 */
let LocalizationService = class LocalizationService {
    constructor(prefix, messageService, _rtl) {
        this.prefix = prefix;
        this.messageService = messageService;
        this._rtl = _rtl;
        this.changes = new BehaviorSubject({ rtl: this._rtl });
        this.dictionary = {};
        if (messageService) {
            this.subscription = messageService.changes
                .pipe(map(({ rtl }) => rtl !== undefined ? rtl : this._rtl), tap(rtl => this._rtl = rtl))
                .subscribe(rtl => {
                this.dictionary = {};
                this.changes.next({ rtl });
            });
        }
    }
    get rtl() {
        return this._rtl;
    }
    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }
    get(shortKey) {
        const key = this.key(shortKey);
        return this.dictionary[key];
    }
    register(shortKey, value, override = false) {
        const key = this.key(shortKey);
        let message = value;
        if (!override) {
            if (this.dictionary.hasOwnProperty(key)) {
                return;
            }
            message = this.defaultValue(key, value);
        }
        this.dictionary[key] = message;
    }
    notifyChanges() {
        this.changes.next({ rtl: this.rtl });
    }
    key(shortKey) {
        return this.prefix + '.' + shortKey;
    }
    defaultValue(key, value) {
        if (!this.messageService) {
            return value;
        }
        const alt = this.messageService.get(key);
        return (alt === undefined) ? value : alt;
    }
};
LocalizationService.ɵfac = function LocalizationService_Factory(t) { return new (t || LocalizationService)(ɵngcc0.ɵɵinject(L10N_PREFIX), ɵngcc0.ɵɵinject(MessageService, 8), ɵngcc0.ɵɵinject(RTL, 8)); };
LocalizationService.ɵprov = ɵngcc0.ɵɵdefineInjectable({ token: LocalizationService, factory: function (t) { return LocalizationService.ɵfac(t); } });
LocalizationService = __decorate([ __param(0, Inject(L10N_PREFIX)),
    __param(1, Optional()),
    __param(2, Optional()), __param(2, Inject(RTL)),
    __metadata("design:paramtypes", [String, MessageService, Boolean])
], LocalizationService);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(MessageService, [{
        type: Injectable
    }], function () { return []; }, null); })();

(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(LocalizationService, [{
        type: Injectable
    }], function () { return [{ type: String, decorators: [{
                type: Inject,
                args: [L10N_PREFIX]
            }] }, { type: MessageService, decorators: [{
                type: Optional
            }] }, { type: Boolean, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [RTL]
            }] }]; }, null); })();

/**
 * Generated bundle index. Do not edit.
 */

export { MessageService, ComponentMessages, L10N_PREFIX, LocalizationService, RTL };

//# sourceMappingURL=data:application/json;charset=utf-8;base64,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